import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Button, Card, Field, NumberInput, PageHead, Spinner, TextInput, useToast } from "../../components/ui";

type Settings = S["PlatformSettings"];

export function AdminPlatformSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const current = useQuery({ queryKey: ["platform-settings"], queryFn: () => unwrap(api.GET("/v1/admin/platform-settings")) });
  const [draft, setDraft] = useState<Settings | null>(null);
  const [newQueue, setNewQueue] = useState("");
  useEffect(() => {
    if (current.data) setDraft(structuredClone(current.data));
  }, [current.data]);
  const save = useMutation({
    mutationFn: (body: S["PlatformSettingsWrite"]) => unwrap(api.PUT("/v1/admin/platform-settings", { body })),
    onSuccess: (data) => {
      qc.setQueryData(["platform-settings"], data);
      qc.invalidateQueries({ queryKey: ["public-pricing"] });
      toast("Platform settings saved");
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (current.isLoading || !draft) return current.error ? <Alert tone="danger">{errorMessage(current.error)}</Alert> : <Spinner />;
  const set = (patch: Partial<Settings>) => setDraft({ ...draft, ...patch });
  const dirty = JSON.stringify(draft) !== JSON.stringify(current.data);
  const { disabled_stages: _ignored, ...writable } = draft;
  const actions = (
    <div className="row">
      <Button variant="ghost" icon={<RotateCcw />} disabled={!dirty} onClick={() => setDraft(structuredClone(current.data!))}>
        Discard
      </Button>
      <Button variant="primary" icon={<Save />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(writable)}>
        Save settings
      </Button>
    </div>
  );
  const int = (v: number | null) => Math.max(0, Math.round(v ?? 0));
  return (
    <>
      <PageHead title="Platform settings" description="Runtime values over the server's environment defaults. Changes apply at once and are audited." actions={actions} />
      <div className="split">
        <div className="stack">
          <Card title="Sign-up" description="What a new company gets when it creates its account.">
            <div className="form-grid">
              <Field label="Free designs at sign-up">
                <NumberInput value={draft.signup_free_designs} min={0} max={1000} step={1} onChange={(v) => set({ signup_free_designs: int(v) })} />
              </Field>
              <Field label="Free designs expire after" hint={draft.signup_free_designs_expire_days == null ? "Never" : undefined}>
                <NumberInput
                  value={draft.signup_free_designs_expire_days}
                  min={1}
                  step={1}
                  suffix="days"
                  placeholder="Never"
                  onChange={(v) => set({ signup_free_designs_expire_days: v == null ? null : Math.max(1, Math.round(v)) })}
                />
              </Field>
              <Field label="Companies per IP per day">
                <NumberInput value={draft.signup_max_orgs_per_ip_per_day} min={0} step={1} onChange={(v) => set({ signup_max_orgs_per_ip_per_day: int(v) })} />
              </Field>
              <Field label="Trial requests per IP per day">
                <NumberInput value={draft.trial_requests_max_per_ip_per_day} min={0} step={1} onChange={(v) => set({ trial_requests_max_per_ip_per_day: int(v) })} />
              </Field>
            </div>
          </Card>
          <Card title="First-purchase discount" description="Applied to a company's first paid order.">
            <div className="form-grid">
              <Field label="Discount">
                <NumberInput value={draft.first_purchase_discount_pct} min={0} max={100} step={1} suffix="%" onChange={(v) => set({ first_purchase_discount_pct: Math.min(100, Math.max(0, v ?? 0)) })} />
              </Field>
              <Field label="On at most" hint="Designs of the first order">
                <NumberInput value={draft.first_purchase_discount_max_designs} min={0} step={1} suffix="designs" onChange={(v) => set({ first_purchase_discount_max_designs: int(v) })} />
              </Field>
            </div>
          </Card>
        </div>
        <Card title="Queue caps" description="Running tasks per queue (heavy, light, io, compute, heavy@lane …), over JOB_MAX_RUNNING_<QUEUE>.">
          <div className="stack">
            {Object.entries(draft.max_running).length === 0 && <p className="muted">No caps stored: the environment defaults apply.</p>}
            {Object.entries(draft.max_running).map(([queue, cap]) => (
              <div key={queue} className="row" style={{ flexWrap: "nowrap" }}>
                <code style={{ flex: 1 }}>{queue}</code>
                <div style={{ width: 140 }}>
                  <NumberInput value={cap} min={0} step={1} suffix="tasks" onChange={(v) => set({ max_running: { ...draft.max_running, [queue]: int(v) } })} />
                </div>
                <Button
                  variant="ghost"
                  icon={<Trash2 />}
                  aria-label={`Remove ${queue}`}
                  onClick={() => {
                    const next = { ...draft.max_running };
                    delete next[queue];
                    set({ max_running: next });
                  }}
                />
              </div>
            ))}
            <div className="input-group">
              <TextInput value={newQueue} onChange={(e) => setNewQueue(e.target.value.trim())} placeholder="queue, e.g. heavy" />
              <Button
                icon={<Plus />}
                disabled={!newQueue || newQueue in draft.max_running}
                onClick={() => {
                  set({ max_running: { ...draft.max_running, [newQueue]: 4 } });
                  setNewQueue("");
                }}
              >
                Add cap
              </Button>
            </div>
            <p className="hint">Per-organisation caps are set on each organisation (Scheduling).</p>
          </div>
        </Card>
      </div>
    </>
  );
}
